import React, { FC, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DrawRequestContext, HeapAnalyticDataContext } from "@app/provider";
import { HEAP_EVENTS } from "@app/provider/types";
import { LoadingContext } from "@natera/platform/lib/components/context";
import { AppointmentDetails } from "@app/components";
import { PatientPortalCtaBanner } from "@app/components/banners";
import ModifyAppointment from "@app/components/sampleDraw/mobilePhlebotomy/appointmentTimeStep/modifyAppointment";
import { routes } from "@app/routing";

import "./viewBloodDrawDetails.scss";

type ViewBloodDrawDetailsProps = {
  token: string;
  isPublicRequest?: boolean;
};

const ViewBloodDrawDetails: FC<ViewBloodDrawDetailsProps> = ({
  token,
  isPublicRequest,
}) => {
  const navigate = useNavigate();
  const { verifiedSampleDrawData, getDrawRequestDetails, isLoading } =
    useContext(DrawRequestContext);
  const { drawRequestDataForHeapEventData } = useContext(
    HeapAnalyticDataContext
  );

  const [modifyAppointment, setModifyAppointment] = useState<boolean>(false);

  const orderUidPublic =
    verifiedSampleDrawData?.sampleInfo.sample?.orderUidPublic;
  const testUid = verifiedSampleDrawData?.sampleInfo.sample?.testUid;
  const testType = verifiedSampleDrawData?.sampleInfo.sample?.testType;
  const testCardStatus =
    verifiedSampleDrawData?.sampleInfo.sample?.testCardStatus;

  // UPP-5865/5890: the DOB verify that landed the returning guest here re-minted
  // a fresh DOB-proof handle. Carry it (+ the same MP token) into sign-up so the
  // DOB screen is skipped; the 5864 gates still validate it, so a stale/expired
  // handle falls back to DOB. Only shown when a handle exists (flag on).
  const intentToken = verifiedSampleDrawData?.intentToken;
  const canCreateAccount = !!intentToken && !!token;

  const handleModifyAppointment = () => setModifyAppointment(true);
  const handleBackToTimeConfirmation = () => setModifyAppointment(false);
  const handleCreateAccount = () => {
    heap.track(HEAP_EVENTS.upp_mp_click_create_account, {
      business_unit: drawRequestDataForHeapEventData?.business_unit,
      test_name: drawRequestDataForHeapEventData?.test_name,
      lims_clinic_id: drawRequestDataForHeapEventData?.lims_clinic_id,
      flow: isPublicRequest ? "public_blood_draw" : "guest_blood_draw",
    });
    navigate(`${routes.signUp}?token=${token}&si=${intentToken}`);
  };

  React.useEffect(() => {
    if (orderUidPublic && testUid) {
      getDrawRequestDetails(orderUidPublic, testUid, token);
    }
  }, []);

  return (
    <LoadingContext isLoading={isLoading}>
      <div className="guest-view-blood-draw">
        <div className="guest-view-blood-draw__content">
          {modifyAppointment ? (
            <ModifyAppointment
              handleBackToTimeConfirmation={handleBackToTimeConfirmation}
              orderUidPublic={orderUidPublic}
              testUid={testUid}
              token={token}
              testType={testType}
            />
          ) : (
            <>
              <AppointmentDetails
                handleModifyAppointment={handleModifyAppointment}
                testCardStatus={testCardStatus}
                hideCancelButton={true}
              />
              {canCreateAccount && (
                <div className="guest-view-blood-draw__create-account">
                  <PatientPortalCtaBanner onClick={handleCreateAccount} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </LoadingContext>
  );
};

export default ViewBloodDrawDetails;
